import React, { useState } from 'react';
import {
  Box, Paper, Typography, IconButton, Button, TablePagination, CircularProgress, Tabs, Tab
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  useDeleteNotificationSettingMutation,
  useGetNotificationSettingsQuery
} from '../../redux/admin/notificationApi';

import NotificationFormModal from './NotificationFormModal';
import MyNotificationsList from './MyNotificationsList';

export default function NotificationList() {
  const { data, isLoading } = useGetNotificationSettingsQuery();
  const [deleteNotificationSetting, { isLoading: isDeleting }] = useDeleteNotificationSettingMutation();
  const settings = data?.settings || [];
  const [activeTab, setActiveTab] = useState('settings');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const paginatedSettings = settings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Delete this notification setting?');
    if (!confirmed) return;

    try {
      await deleteNotificationSetting(id).unwrap();
      const nextTotal = Math.max(settings.length - 1, 0);
      const maxPage = Math.max(Math.ceil(nextTotal / rowsPerPage) - 1, 0);
      if (page > maxPage) {
        setPage(maxPage);
      }
    } catch (error) {
      console.error('Failed to delete notification setting:', error);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4">Notifications</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Settings aur aapke khud ke notifications dono yahin se manage kar sakte ho.
          </Typography>
        </Box>

        {activeTab === 'settings' ? (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => {
              setEditData(null);
              setOpen(true);
            }}
          >
            Add
          </Button>
        ) : null}
      </Box>

      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{
            px: 2,
            pt: 1,
            borderBottom: '1px solid #e5e7eb',
            '& .MuiTab-root': {
              fontWeight: 800,
              textTransform: 'none',
              minHeight: 48,
            },
          }}
        >
          <Tab value="settings" label="Notification Settings" />
          <Tab value="myNotifications" label="My Notification" />
        </Tabs>

        {activeTab === 'settings' ? (
          <>
            {isLoading ? (
              <Box p={4} display="flex" justifyContent="center">
                <CircularProgress size={28} />
              </Box>
            ) : paginatedSettings.map((row) => (
              <Box
                key={row._id}
                display="grid"
                gridTemplateColumns="80px 1fr 140px 140px 120px 120px"
                alignItems="center"
                p={2}
                borderBottom="1px solid #eee"
              >
                <Typography>{row._id.slice(-4)}</Typography>
                <Typography fontWeight={600}>{row.alarmType}</Typography>
                <Typography>{row.allDevices ? 'Yes' : 'No'}</Typography>
                <Typography>{row.channels?.join(', ')}</Typography>
                <Typography>{row.sound}</Typography>

                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                  <IconButton
                    onClick={() => {
                      setEditData(row);
                      setOpen(true);
                    }}
                  >
                    <EditIcon color="primary" />
                  </IconButton>

                  <IconButton
                    color="error"
                    disabled={isDeleting}
                    onClick={() => handleDelete(row._id)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}

            {!isLoading && settings.length === 0 ? (
              <Box p={4}>
                <Typography color="text.secondary">No notification settings found.</Typography>
              </Box>
            ) : null}

            <TablePagination
              component="div"
              count={settings.length}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        ) : (
          <Box sx={{ p: 0 }}>
            <MyNotificationsList />
          </Box>
        )}
      </Paper>

      <NotificationFormModal
        open={open}
        onClose={() => setOpen(false)}
        editData={editData}
      />
    </Box>
  );
}
